import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../lib/api';
import {
  clearAdminToken,
  getAdminAuthHeaders,
  isAdminTokenExpired,
  readAdminToken,
} from '../lib/adminAuth';
import {
  CONTENT_TEMPLATES,
  normalizeContentBlock,
  normalizeContentBlocks,
  parseContentValue,
  stringifyContentValue,
  unwrapContentPayload,
} from '../lib/content';
import { formatPostDate, normalizePost, normalizePosts } from '../lib/posts';
import { isStripeHostedCheckoutUrl } from '../lib/store';

const EMPTY_POST_FORM = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  featuredImage: '',
  published: false,
};

const PRODUCT_STATUS_OPTIONS = ['draft', 'active', 'archived'];
const KNOWN_CONTENT_SECTIONS = Object.keys(CONTENT_TEMPLATES);

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return 'Not available';
  }

  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function parseListInput(value) {
  return `${value ?? ''}`
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isHttpsUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidProductAssetReference(value) {
  const normalizedValue = `${value ?? ''}`.trim();

  if (!normalizedValue) {
    return true;
  }

  return normalizedValue.startsWith('/') || isHttpsUrl(normalizedValue);
}

function validateProductInput(productForm) {
  const slug = productForm.slug.trim();
  const checkoutUrl = productForm.checkoutUrl.trim();
  const featuredImage = productForm.featuredImage.trim();
  const gallery = parseListInput(productForm.gallery);
  const priceCents = Number.parseInt(productForm.priceCents, 10);
  const compareAtPriceCents = Number.parseInt(productForm.compareAtPriceCents, 10);
  const inventoryCount = Number.parseInt(productForm.inventoryCount, 10);
  const sortOrder = Number.parseInt(productForm.sortOrder, 10);
  const currencyCode = productForm.currencyCode.trim().toUpperCase();
  const status = productForm.status.trim().toLowerCase();

  if (slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Slug can only contain lowercase letters, numbers, and hyphens.';
  }

  if (!/^[A-Z]{3}$/.test(currencyCode)) {
    return 'Currency must be a 3-letter ISO code like USD.';
  }

  if (!Number.isInteger(priceCents) || priceCents < 0) {
    return 'Price must be 0 or greater in cents.';
  }

  if (productForm.compareAtPriceCents.trim()) {
    if (!Number.isInteger(compareAtPriceCents) || compareAtPriceCents < 0) {
      return 'Compare-at price must be 0 or greater in cents.';
    }

    if (compareAtPriceCents < priceCents) {
      return 'Compare-at price must be greater than or equal to the base price.';
    }
  }

  if (!Number.isInteger(inventoryCount) || inventoryCount < 0) {
    return 'Inventory count must be 0 or greater.';
  }

  if (!Number.isInteger(sortOrder) || sortOrder < 0) {
    return 'Sort order must be 0 or greater.';
  }

  if (checkoutUrl && (!isHttpsUrl(checkoutUrl) || !isStripeHostedCheckoutUrl(checkoutUrl))) {
    return 'Payment Link must be a Stripe-hosted https://buy.stripe.com/... or https://pay.stripe.com/... URL.';
  }

  if (!isValidProductAssetReference(featuredImage)) {
    return 'Featured Image must be a root-relative asset path or an https URL.';
  }

  const invalidGalleryItem = gallery.find((item) => !isValidProductAssetReference(item));

  if (invalidGalleryItem) {
    return 'Each gallery image must be a root-relative asset path or an https URL.';
  }

  if (status === 'active') {
    if (priceCents <= 0) {
      return 'Active products need a price greater than 0 cents.';
    }

    if (!productForm.shortDescription.trim()) {
      return 'Active products need a short description.';
    }

    if (!featuredImage) {
      return 'Active products need a featured image.';
    }
  }

  return null;
}

function sortProducts(products = []) {
  return [...products].sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) {
      return left.sortOrder - right.sortOrder;
    }

    const leftTime = new Date(left.createdAt ?? 0).getTime() || 0;
    const rightTime = new Date(right.createdAt ?? 0).getTime() || 0;

    if (leftTime !== rightTime) {
      return rightTime - leftTime;
    }

    return left.name.localeCompare(right.name);
  });
}

function normalizeAdminProduct(product) {
  if (!product || typeof product !== 'object') {
    return null;
  }

  const id = product.id ?? product.ID ?? null;

  return {
    ...product,
    id: id == null ? null : String(id),
    slug: `${product.slug ?? ''}`.trim(),
    name: `${product.name ?? ''}`.trim(),
    shortDescription: `${product.shortDescription ?? product.short_description ?? ''}`.trim(),
    description: `${product.description ?? ''}`.trim(),
    category: `${product.category ?? ''}`.trim(),
    sku: `${product.sku ?? ''}`.trim(),
    checkoutUrl: `${product.checkoutUrl ?? product.checkout_url ?? ''}`.trim(),
    priceCents: Number(product.priceCents ?? product.price_cents ?? 0) || 0,
    compareAtPriceCents:
      product.compareAtPriceCents ?? product.compare_at_price_cents ?? null,
    currencyCode: `${product.currencyCode ?? product.currency_code ?? 'USD'}`
      .trim()
      .toUpperCase(),
    featuredImage: `${product.featuredImage ?? product.featured_image ?? ''}`.trim(),
    gallery: Array.isArray(product.gallery) ? product.gallery.filter(Boolean) : [],
    tags: Array.isArray(product.tags) ? product.tags.filter(Boolean) : [],
    featured: Boolean(product.featured),
    status: `${product.status ?? 'draft'}`.trim().toLowerCase() || 'draft',
    inventoryCount: Number(product.inventoryCount ?? product.inventory_count ?? 0) || 0,
    sortOrder: Number(product.sortOrder ?? product.sort_order ?? 0) || 0,
    createdAt: product.createdAt ?? product.created_at ?? null,
    updatedAt: product.updatedAt ?? product.updated_at ?? null,
  };
}

function buildEmptyProductForm(product = null) {
  const normalized = normalizeAdminProduct(product);

  if (!normalized) {
    return {
      name: '',
      slug: '',
      shortDescription: '',
      description: '',
      category: '',
      sku: '',
      checkoutUrl: '',
      priceCents: '0',
      compareAtPriceCents: '',
      currencyCode: 'USD',
      featuredImage: '',
      gallery: '',
      tags: '',
      featured: false,
      status: 'draft',
      inventoryCount: '0',
      sortOrder: '0',
    };
  }

  return {
    name: normalized.name,
    slug: normalized.slug,
    shortDescription: normalized.shortDescription,
    description: normalized.description,
    category: normalized.category,
    sku: normalized.sku,
    checkoutUrl: normalized.checkoutUrl,
    priceCents: `${normalized.priceCents}`,
    compareAtPriceCents:
      normalized.compareAtPriceCents == null ? '' : `${normalized.compareAtPriceCents}`,
    currencyCode: normalized.currencyCode,
    featuredImage: normalized.featuredImage,
    gallery: normalized.gallery.join('\n'),
    tags: normalized.tags.join(', '),
    featured: normalized.featured,
    status: normalized.status,
    inventoryCount: `${normalized.inventoryCount}`,
    sortOrder: `${normalized.sortOrder}`,
  };
}

function buildProductPayload(productForm) {
  const compareAtValue = productForm.compareAtPriceCents.trim();

  return {
    name: productForm.name.trim(),
    slug: productForm.slug.trim() || undefined,
    shortDescription: productForm.shortDescription.trim(),
    description: productForm.description.trim(),
    category: productForm.category.trim(),
    sku: productForm.sku.trim() || null,
    checkoutUrl: productForm.checkoutUrl.trim() || null,
    priceCents: Number.parseInt(productForm.priceCents, 10) || 0,
    compareAtPriceCents: compareAtValue ? Number.parseInt(compareAtValue, 10) : null,
    currencyCode: productForm.currencyCode.trim().toUpperCase() || 'USD',
    featuredImage: productForm.featuredImage.trim() || null,
    gallery: parseListInput(productForm.gallery),
    tags: parseListInput(productForm.tags),
    featured: Boolean(productForm.featured),
    status: productForm.status,
    inventoryCount: Number.parseInt(productForm.inventoryCount, 10) || 0,
    sortOrder: Number.parseInt(productForm.sortOrder, 10) || 0,
  };
}

function buildEmptyContentForm(block = null) {
  const normalized = normalizeContentBlock(block);
  const section = normalized?.section ?? '';
  const template = section ? CONTENT_TEMPLATES[section] : null;

  return {
    section,
    title: normalized?.title ?? '',
    type: normalized?.type ?? '',
    content: normalized?.content ?? (template ? stringifyContentValue(template) : ''),
  };
}

function getPreviewText(value) {
  const parsedValue = parseContentValue(value, '');

  if (typeof parsedValue === 'string') {
    return parsedValue.length > 120 ? `${parsedValue.slice(0, 117)}...` : parsedValue;
  }

  const serialized = stringifyContentValue(parsedValue);
  return serialized.length > 120 ? `${serialized.slice(0, 117)}...` : serialized;
}

function validateContentInput(contentForm) {
  if (!contentForm.section.trim()) {
    return 'Section is required.';
  }

  if (!contentForm.content.trim()) {
    return 'Content is required.';
  }

  try {
    parseContentValue(contentForm.content, null);
    return null;
  } catch {
    return 'Content must be valid JSON or plain text.';
  }
}

function formatProductPrice(product) {
  const amount = (product.priceCents || 0) / 100;
  return `${product.currencyCode} ${amount.toFixed(2)}`;
}

function getProductCheckoutModeLabel(product) {
  if (isStripeHostedCheckoutUrl(product.checkoutUrl)) {
    return 'Direct Stripe Payment Link';
  }

  if (product.status === 'active') {
    return 'Hosted Stripe Checkout';
  }

  return 'Not live';
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-2 text-sm text-gray-700">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-gray-900"
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-gray-900"
    />
  );
}

function ProductEditor({
  title,
  submitLabel,
  productForm,
  setProductForm,
  onSubmit,
  onCancel,
  submitting,
}) {
  const hasFeaturedImage = productForm.featuredImage.trim().length > 0;

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-medium text-gray-500 transition hover:text-gray-900"
          >
            Cancel
          </button>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name" htmlFor="product-name">
          <TextInput
            id="product-name"
            value={productForm.name}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, name: event.target.value }))
            }
            required
          />
        </Field>

        <Field label="Slug" htmlFor="product-slug">
          <TextInput
            id="product-slug"
            value={productForm.slug}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, slug: event.target.value }))
            }
            placeholder="Optional"
          />
        </Field>

        <Field label="Category" htmlFor="product-category">
          <TextInput
            id="product-category"
            value={productForm.category}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, category: event.target.value }))
            }
          />
        </Field>

        <Field label="SKU" htmlFor="product-sku">
          <TextInput
            id="product-sku"
            value={productForm.sku}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, sku: event.target.value }))
            }
          />
        </Field>

        <Field label="Price (cents)" htmlFor="product-price">
          <TextInput
            id="product-price"
            type="number"
            min="0"
            value={productForm.priceCents}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, priceCents: event.target.value }))
            }
            required
          />
        </Field>

        <Field label="Compare At (cents)" htmlFor="product-compare-price">
          <TextInput
            id="product-compare-price"
            type="number"
            min="0"
            value={productForm.compareAtPriceCents}
            onChange={(event) =>
              setProductForm((current) => ({
                ...current,
                compareAtPriceCents: event.target.value,
              }))
            }
          />
        </Field>

        <Field label="Currency" htmlFor="product-currency">
          <TextInput
            id="product-currency"
            value={productForm.currencyCode}
            onChange={(event) =>
              setProductForm((current) => ({
                ...current,
                currencyCode: event.target.value.toUpperCase(),
              }))
            }
            maxLength={3}
          />
        </Field>

        <Field label="Status" htmlFor="product-status">
          <select
            id="product-status"
            value={productForm.status}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, status: event.target.value }))
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-gray-900"
          >
            {PRODUCT_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Inventory Count" htmlFor="product-inventory">
          <TextInput
            id="product-inventory"
            type="number"
            min="0"
            value={productForm.inventoryCount}
            onChange={(event) =>
              setProductForm((current) => ({
                ...current,
                inventoryCount: event.target.value,
              }))
            }
          />
        </Field>

        <Field label="Sort Order" htmlFor="product-sort-order">
          <TextInput
            id="product-sort-order"
            type="number"
            min="0"
            value={productForm.sortOrder}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, sortOrder: event.target.value }))
            }
          />
        </Field>

        <Field label="Stripe Payment Link" htmlFor="product-checkout-url">
          <TextInput
            id="product-checkout-url"
            value={productForm.checkoutUrl}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, checkoutUrl: event.target.value }))
            }
            placeholder="https://buy.stripe.com/..."
          />
          <p className="text-xs leading-5 text-gray-500">
            Optional. Leave blank to use hosted Stripe Checkout. If provided, this must be a
            Stripe-hosted <code>buy.stripe.com</code> or <code>pay.stripe.com</code> link.
          </p>
        </Field>

        <Field label="Featured Image" htmlFor="product-featured-image">
          <TextInput
            id="product-featured-image"
            value={productForm.featuredImage}
            onChange={(event) =>
              setProductForm((current) => ({
                ...current,
                featuredImage: event.target.value,
              }))
            }
            placeholder="/api/assets/product-image.svg"
          />
          <p className="text-xs leading-5 text-gray-500">
            Use a root-relative asset like <code>/api/assets/...</code> or an <code>https</code>{' '}
            image URL.
          </p>
        </Field>
      </div>

      {hasFeaturedImage ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
          <div className="border-b border-gray-200 px-4 py-3 text-xs font-medium uppercase tracking-[0.18em] text-gray-500">
            Featured Image Preview
          </div>
          <img
            src={productForm.featuredImage.trim()}
            alt="Featured product preview"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      ) : null}

      <Field label="Short Description" htmlFor="product-short-description">
        <TextArea
          id="product-short-description"
          value={productForm.shortDescription}
          onChange={(event) =>
            setProductForm((current) => ({
              ...current,
              shortDescription: event.target.value,
            }))
          }
          rows={3}
        />
      </Field>

      <Field label="Description" htmlFor="product-description">
        <TextArea
          id="product-description"
          value={productForm.description}
          onChange={(event) =>
            setProductForm((current) => ({ ...current, description: event.target.value }))
          }
          rows={6}
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Gallery" htmlFor="product-gallery">
          <TextArea
            id="product-gallery"
            value={productForm.gallery}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, gallery: event.target.value }))
            }
            rows={5}
            placeholder="One URL per line or comma-separated"
          />
        </Field>

        <Field label="Tags" htmlFor="product-tags">
          <TextArea
            id="product-tags"
            value={productForm.tags}
            onChange={(event) =>
              setProductForm((current) => ({ ...current, tags: event.target.value }))
            }
            rows={5}
            placeholder="One tag per line or comma-separated"
          />
        </Field>
      </div>

      <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          checked={productForm.featured}
          onChange={(event) =>
            setProductForm((current) => ({ ...current, featured: event.target.checked }))
          }
          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
        />
        Featured product
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Saving...' : submitLabel}
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateContent, setShowCreateContent] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [postForm, setPostForm] = useState(EMPTY_POST_FORM);
  const [productForm, setProductForm] = useState(buildEmptyProductForm());
  const [contentForm, setContentForm] = useState(buildEmptyContentForm());
  const [savingPost, setSavingPost] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingContent, setSavingContent] = useState(false);

  const postUrl = buildApiUrl('/admin/posts');
  const productUrl = buildApiUrl('/admin/products');
  const contentUrl = buildApiUrl('/content');

  useEffect(() => {
    const token = readAdminToken();

    if (isAdminTokenExpired(token)) {
      clearAdminToken();
      navigate('/admin', { replace: true });
      return;
    }

    let isMounted = true;

    async function loadDashboard() {
      try {
        setLoading(true);
        setError(null);

        const [postResponse, productResponse, contentResponse] = await Promise.all([
          axios.get(postUrl, { headers: getAdminAuthHeaders() }),
          axios.get(productUrl, { headers: getAdminAuthHeaders() }),
          axios.get(contentUrl, { headers: getAdminAuthHeaders() }),
        ]);

        if (!isMounted) {
          return;
        }

        setPosts(normalizePosts(postResponse.data));
        setProducts(
          sortProducts(
            (Array.isArray(productResponse.data) ? productResponse.data : [])
              .map(normalizeAdminProduct)
              .filter(Boolean)
          )
        );
        setContentBlocks(
          normalizeContentBlocks(unwrapContentPayload(contentResponse.data))
        );
      } catch (err) {
        if (!isMounted) {
          return;
        }

        if (err.response?.status === 401 || err.response?.status === 403) {
          clearAdminToken();
          navigate('/admin', { replace: true });
          return;
        }

        setError(err.response?.data?.error || err.message || 'Failed to load dashboard.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [contentUrl, navigate, postUrl, productUrl]);

  function resetPostEditor() {
    setEditingPost(null);
    setPostForm(EMPTY_POST_FORM);
    setShowCreatePost(false);
  }

  function resetProductEditor() {
    setEditingProduct(null);
    setProductForm(buildEmptyProductForm());
    setShowCreateProduct(false);
  }

  function resetContentEditor() {
    setEditingContent(null);
    setContentForm(buildEmptyContentForm());
    setShowCreateContent(false);
  }

  function handleLogout() {
    clearAdminToken();
    navigate('/admin', { replace: true });
  }

  async function handleCreatePost(event) {
    event.preventDefault();
    setSavingPost(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.post(
        postUrl,
        {
          title: postForm.title.trim(),
          slug: postForm.slug.trim() || null,
          excerpt: postForm.excerpt.trim() || null,
          content: postForm.content.trim(),
          featured_image: postForm.featuredImage.trim() || null,
          published: postForm.published,
        },
        { headers: getAdminAuthHeaders() }
      );

      const nextPost = normalizePost(response.data);
      setPosts((current) => [nextPost, ...current].filter(Boolean));
      resetPostEditor();
      setNotice('Blog post created.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create post.');
    } finally {
      setSavingPost(false);
    }
  }

  async function handleEditPost(post) {
    setError(null);
    setNotice(null);

    let editablePost = normalizePost(post);

    if (!editablePost?.content && post?.id) {
      try {
        const response = await axios.get(buildApiUrl(`/blog/${post.id}`), {
          headers: getAdminAuthHeaders(),
        });
        editablePost = normalizePost(response.data) ?? editablePost;
      } catch {
        editablePost = normalizePost(post);
      }
    }

    setEditingPost(editablePost);
    setPostForm({
      title: editablePost?.title ?? '',
      slug: editablePost?.slug ?? '',
      excerpt: editablePost?.excerpt ?? '',
      content: editablePost?.content ?? '',
      featuredImage: editablePost?.featured_image ?? editablePost?.featuredImage ?? '',
      published: Boolean(editablePost?.published),
    });
    setShowCreatePost(false);
  }

  async function handleUpdatePost(event) {
    event.preventDefault();

    if (!editingPost?.id) {
      return;
    }

    setSavingPost(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.put(
        `${postUrl}/${editingPost.id}`,
        {
          title: postForm.title.trim(),
          slug: postForm.slug.trim() || null,
          excerpt: postForm.excerpt.trim() || null,
          content: postForm.content.trim(),
          featured_image: postForm.featuredImage.trim() || null,
          published: postForm.published,
        },
        { headers: getAdminAuthHeaders() }
      );

      const updatedPost = normalizePost(response.data);
      setPosts((current) =>
        current.map((post) => (post.id === updatedPost.id ? updatedPost : post))
      );
      resetPostEditor();
      setNotice('Blog post updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update post.');
    } finally {
      setSavingPost(false);
    }
  }

  async function handleDeletePost(postId) {
    if (!window.confirm('Delete this blog post?')) {
      return;
    }

    setError(null);
    setNotice(null);

    try {
      await axios.delete(`${postUrl}/${postId}`, { headers: getAdminAuthHeaders() });
      setPosts((current) => current.filter((post) => post.id !== postId));

      if (editingPost?.id === postId) {
        resetPostEditor();
      }

      setNotice('Blog post deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete post.');
    }
  }

  async function handleCreateProduct(event) {
    event.preventDefault();
    const validationMessage = validateProductInput(productForm);

    if (validationMessage) {
      setError(validationMessage);
      setNotice(null);
      return;
    }

    setSavingProduct(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.post(productUrl, buildProductPayload(productForm), {
        headers: getAdminAuthHeaders(),
      });

      const nextProduct = normalizeAdminProduct(response.data);
      setProducts((current) => sortProducts([nextProduct, ...current].filter(Boolean)));
      resetProductEditor();
      setNotice('Product created.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create product.');
    } finally {
      setSavingProduct(false);
    }
  }

  function handleEditProduct(product) {
    setEditingProduct(product);
    setProductForm(buildEmptyProductForm(product));
    setShowCreateProduct(false);
    setError(null);
    setNotice(null);
  }

  async function handleUpdateProduct(event) {
    event.preventDefault();

    if (!editingProduct?.id) {
      return;
    }

    const validationMessage = validateProductInput(productForm);

    if (validationMessage) {
      setError(validationMessage);
      setNotice(null);
      return;
    }

    setSavingProduct(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.put(
        `${productUrl}/${editingProduct.id}`,
        buildProductPayload(productForm),
        { headers: getAdminAuthHeaders() }
      );

      const updatedProduct = normalizeAdminProduct(response.data);
      setProducts((current) =>
        sortProducts(
          current.map((product) =>
            product.id === updatedProduct.id ? updatedProduct : product
          )
        )
      );
      resetProductEditor();
      setNotice('Product updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update product.');
    } finally {
      setSavingProduct(false);
    }
  }

  async function handleDeleteProduct(productId) {
    if (!window.confirm('Delete this product?')) {
      return;
    }

    setError(null);
    setNotice(null);

    try {
      await axios.delete(`${productUrl}/${productId}`, { headers: getAdminAuthHeaders() });
      setProducts((current) => current.filter((product) => product.id !== productId));

      if (editingProduct?.id === productId) {
        resetProductEditor();
      }

      setNotice('Product deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete product.');
    }
  }

  async function handleCreateContent(event) {
    event.preventDefault();
    const validationMessage = validateContentInput(contentForm);

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSavingContent(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.post(
        contentUrl,
        {
          section: contentForm.section.trim(),
          title: contentForm.title.trim(),
          content: contentForm.content.trim(),
          type: contentForm.type.trim() || null,
        },
        { headers: getAdminAuthHeaders() }
      );

      const nextBlock = normalizeContentBlock(response.data?.data ?? response.data);
      setContentBlocks((current) => [nextBlock, ...current].filter(Boolean));
      resetContentEditor();
      setNotice('Content block created.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create content.');
    } finally {
      setSavingContent(false);
    }
  }

  function handleEditContent(block) {
    setEditingContent(block);
    setContentForm(buildEmptyContentForm(block));
    setShowCreateContent(false);
    setError(null);
    setNotice(null);
  }

  async function handleUpdateContent(event) {
    event.preventDefault();
    const validationMessage = validateContentInput(contentForm);

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    if (!editingContent?.id) {
      return;
    }

    setSavingContent(true);
    setError(null);
    setNotice(null);

    try {
      const response = await axios.put(
        `${contentUrl}/${editingContent.id}`,
        {
          section: contentForm.section.trim(),
          title: contentForm.title.trim(),
          content: contentForm.content.trim(),
          type: contentForm.type.trim() || null,
        },
        { headers: getAdminAuthHeaders() }
      );

      const updatedBlock = normalizeContentBlock(response.data?.data ?? response.data);
      setContentBlocks((current) =>
        current.map((block) => (block.id === updatedBlock.id ? updatedBlock : block))
      );
      resetContentEditor();
      setNotice('Content block updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update content.');
    } finally {
      setSavingContent(false);
    }
  }

  async function handleDeleteContent(contentId) {
    if (!window.confirm('Delete this content block?')) {
      return;
    }

    setError(null);
    setNotice(null);

    try {
      await axios.delete(`${contentUrl}/${contentId}`, { headers: getAdminAuthHeaders() });
      setContentBlocks((current) => current.filter((block) => block.id !== contentId));

      if (editingContent?.id === contentId) {
        resetContentEditor();
      }

      setNotice('Content block deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete content.');
    }
  }

  function applyTemplate(section) {
    const template = CONTENT_TEMPLATES[section];

    setContentForm((current) => ({
      ...current,
      section,
      content: template ? stringifyContentValue(template) : current.content,
    }));
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
          Loading admin dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">
            Admin
          </p>
          <h1 className="text-3xl font-semibold text-gray-900">Dashboard</h1>
          <p className="max-w-2xl text-sm leading-6 text-gray-600">
            Manage store products, blog posts, and structured site content here.
          </p>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-900 hover:text-gray-900"
        >
          Log out
        </button>
      </div>

      {error ? (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {notice}
        </div>
      ) : null}

      <div className="space-y-8">
        <section className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Store Products</h2>
              <p className="mt-1 text-sm text-gray-600">
                Create and manage products for <code>/store</code>. Only products with
                <code> active </code>
                status appear on the public storefront. Hosted Stripe Checkout powers the live
                storefront by default, and optional direct links must be Stripe-hosted Payment
                Links.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowCreateProduct((current) => !current);
                setEditingProduct(null);
                setProductForm(buildEmptyProductForm());
              }}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
            >
              {showCreateProduct ? 'Close Product Form' : 'New Product'}
            </button>
          </div>

          <div className="space-y-5">
            {showCreateProduct ? (
              <ProductEditor
                title="Create Product"
                submitLabel="Create Product"
                productForm={productForm}
                setProductForm={setProductForm}
                onSubmit={handleCreateProduct}
                submitting={savingProduct}
              />
            ) : null}

            {editingProduct ? (
              <ProductEditor
                title={`Edit Product: ${editingProduct.name}`}
                submitLabel="Update Product"
                productForm={productForm}
                setProductForm={setProductForm}
                onSubmit={handleUpdateProduct}
                onCancel={resetProductEditor}
                submitting={savingProduct}
              />
            ) : null}

            {products.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                No products yet.
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {products.map((product) => (
                  <article
                    key={product.id}
                    className="rounded-2xl border border-gray-200 bg-white p-5"
                  >
                    <div className="mb-3 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-500">
                          {product.status}
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-gray-900">
                          {product.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {formatProductPrice(product)}
                        </p>
                      </div>

                      {product.featured ? (
                        <span className="rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white">
                          Featured
                        </span>
                      ) : null}
                    </div>

                    <p className="mb-4 text-sm leading-6 text-gray-600">
                      {product.shortDescription || 'No short description yet.'}
                    </p>

                    <dl className="grid gap-2 text-sm text-gray-600">
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Slug:</dt>
                        <dd>{product.slug || 'Auto-generated'}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Inventory:</dt>
                        <dd>{product.inventoryCount}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Sort:</dt>
                        <dd>{product.sortOrder}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Checkout:</dt>
                        <dd>{getProductCheckoutModeLabel(product)}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Payment Link:</dt>
                        <dd className="break-all">{product.checkoutUrl || 'Hosted checkout default'}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-gray-900">Updated:</dt>
                        <dd>{formatDateTime(product.updatedAt || product.createdAt)}</dd>
                      </div>
                    </dl>

                    <div className="mt-5 flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleEditProduct(product)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-900 hover:text-gray-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition hover:border-red-500 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Blog Posts</h2>
              <p className="mt-1 text-sm text-gray-600">
                Create and update posts without leaving the admin dashboard.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowCreatePost((current) => !current);
                setEditingPost(null);
                setPostForm(EMPTY_POST_FORM);
              }}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
            >
              {showCreatePost ? 'Close Post Form' : 'New Post'}
            </button>
          </div>

          <div className="space-y-5">
            {showCreatePost || editingPost ? (
              <form
                onSubmit={editingPost ? handleUpdatePost : handleCreatePost}
                className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5"
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingPost ? `Edit Post: ${editingPost.title}` : 'Create Post'}
                  </h3>
                  {(showCreatePost || editingPost) && (
                    <button
                      type="button"
                      onClick={resetPostEditor}
                      className="text-sm font-medium text-gray-500 transition hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Title" htmlFor="post-title">
                    <TextInput
                      id="post-title"
                      value={postForm.title}
                      onChange={(event) =>
                        setPostForm((current) => ({ ...current, title: event.target.value }))
                      }
                      required
                    />
                  </Field>

                  <Field label="Slug" htmlFor="post-slug">
                    <TextInput
                      id="post-slug"
                      value={postForm.slug}
                      onChange={(event) =>
                        setPostForm((current) => ({ ...current, slug: event.target.value }))
                      }
                    />
                  </Field>
                </div>

                <Field label="Excerpt" htmlFor="post-excerpt">
                  <TextArea
                    id="post-excerpt"
                    value={postForm.excerpt}
                    onChange={(event) =>
                      setPostForm((current) => ({ ...current, excerpt: event.target.value }))
                    }
                    rows={3}
                  />
                </Field>

                <Field label="Featured Image" htmlFor="post-featured-image">
                  <TextInput
                    id="post-featured-image"
                    value={postForm.featuredImage}
                    onChange={(event) =>
                      setPostForm((current) => ({
                        ...current,
                        featuredImage: event.target.value,
                      }))
                    }
                  />
                </Field>

                <Field label="Content" htmlFor="post-content">
                  <TextArea
                    id="post-content"
                    value={postForm.content}
                    onChange={(event) =>
                      setPostForm((current) => ({ ...current, content: event.target.value }))
                    }
                    rows={12}
                    required
                  />
                </Field>

                <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={postForm.published}
                    onChange={(event) =>
                      setPostForm((current) => ({
                        ...current,
                        published: event.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  Published
                </label>

                <button
                  type="submit"
                  disabled={savingPost}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingPost
                    ? 'Saving...'
                    : editingPost
                      ? 'Update Post'
                      : 'Create Post'}
                </button>
              </form>
            ) : null}

            {posts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                No blog posts yet.
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {posts.map((post) => (
                  <article key={post.id} className="rounded-2xl border border-gray-200 bg-white p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                        <p className="mt-1 text-sm text-gray-500">{formatPostDate(post.createdAt)}</p>
                      </div>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          post.published
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>

                    <p className="mt-3 text-sm leading-6 text-gray-600">
                      {post.excerpt || 'No excerpt yet.'}
                    </p>

                    <div className="mt-5 flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleEditPost(post)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-900 hover:text-gray-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePost(post.id)}
                        className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition hover:border-red-500 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Site Content</h2>
              <p className="mt-1 text-sm text-gray-600">
                Update structured content blocks used across the site.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowCreateContent((current) => !current);
                setEditingContent(null);
                setContentForm(buildEmptyContentForm());
              }}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
            >
              {showCreateContent ? 'Close Content Form' : 'New Content Block'}
            </button>
          </div>

          <div className="space-y-5">
            {showCreateContent || editingContent ? (
              <form
                onSubmit={editingContent ? handleUpdateContent : handleCreateContent}
                className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5"
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingContent ? `Edit Content: ${editingContent.section}` : 'Create Content'}
                  </h3>
                  {(showCreateContent || editingContent) && (
                    <button
                      type="button"
                      onClick={resetContentEditor}
                      className="text-sm font-medium text-gray-500 transition hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Section" htmlFor="content-section">
                    <TextInput
                      id="content-section"
                      list="known-content-sections"
                      value={contentForm.section}
                      onChange={(event) =>
                        setContentForm((current) => ({
                          ...current,
                          section: event.target.value,
                        }))
                      }
                      required
                    />
                    <datalist id="known-content-sections">
                      {KNOWN_CONTENT_SECTIONS.map((section) => (
                        <option key={section} value={section} />
                      ))}
                    </datalist>
                  </Field>

                  <Field label="Type" htmlFor="content-type">
                    <TextInput
                      id="content-type"
                      value={contentForm.type}
                      onChange={(event) =>
                        setContentForm((current) => ({ ...current, type: event.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </Field>
                </div>

                <Field label="Title" htmlFor="content-title">
                  <TextInput
                    id="content-title"
                    value={contentForm.title}
                    onChange={(event) =>
                      setContentForm((current) => ({ ...current, title: event.target.value }))
                    }
                    placeholder="Optional"
                  />
                </Field>

                <div className="flex flex-wrap gap-2">
                  {KNOWN_CONTENT_SECTIONS.map((section) => (
                    <button
                      key={section}
                      type="button"
                      onClick={() => applyTemplate(section)}
                      className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition hover:border-gray-900 hover:text-gray-900"
                    >
                      Use {section} template
                    </button>
                  ))}
                </div>

                <Field label="Content" htmlFor="content-body">
                  <TextArea
                    id="content-body"
                    value={contentForm.content}
                    onChange={(event) =>
                      setContentForm((current) => ({ ...current, content: event.target.value }))
                    }
                    rows={12}
                    required
                  />
                </Field>

                <button
                  type="submit"
                  disabled={savingContent}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingContent
                    ? 'Saving...'
                    : editingContent
                      ? 'Update Content'
                      : 'Create Content'}
                </button>
              </form>
            ) : null}

            {contentBlocks.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                No content blocks yet.
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {contentBlocks.map((block) => (
                  <article key={block.id} className="rounded-2xl border border-gray-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-gray-900">{block.section}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {block.title || 'Untitled content block'}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-gray-600">
                      {getPreviewText(block.content)}
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-[0.18em] text-gray-400">
                      Updated {formatDateTime(block.updatedAt || block.createdAt)}
                    </p>

                    <div className="mt-5 flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleEditContent(block)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-900 hover:text-gray-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteContent(block.id)}
                        className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition hover:border-red-500 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
